import {
  useEffect,
  useState,
} from "react";

import {
  useSearchParams,
} from "react-router-dom";

import {
  useQueryClient,
} from "@tanstack/react-query";

import {
  api,
  formatApiErrorDetail,
} from "@/lib/api";

import {
  useFarm,
} from "@/context/FarmContext";

import {
  PageHeader,
} from "@/components/Page";

import {
  Card,
} from "@/components/ui/card";

import {
  Button,
} from "@/components/ui/button";

import {
  Input,
} from "@/components/ui/input";

import {
  Label,
} from "@/components/ui/label";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import {
  toast,
} from "sonner";

import {
  Save,
  Plus,
  Trash2,
  Upload,
  FileText,
  Sprout,
  MapPin,
  Target,
  Tractor,
  CheckCircle2,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Info,
  CloudSun,
} from "lucide-react";


const empty = {
  name: "",
  grazing_area: 0,
  cow_numbers: 0,
  target_milk_yield: 0,
  intake_per_cow: 0,
  herd_type: "dairy",
  currency: "EUR",
  latitude: 53.35,
  longitude: -6.26,
  grass_basis: "total_cover",
  grass_residual: "",
  pre_grazing_min: "",
  pre_grazing_max: "",
};


const BASIS_DEFAULTS = {
  total_cover: {
    residual: 1500,
    min: 2800,
    max: 3000,
  },

  above_4cm: {
    residual: 0,
    min: 1300,
    max: 1600,
  },
};


function SectionHeader({
  icon: Icon,
  title,
  subtitle,
  open,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full flex items-center justify-between gap-4 p-4 sm:p-5 text-left"
    >
      <div className="flex items-center gap-3 min-w-0">

        <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center shrink-0">

          <Icon className="h-5 w-5 text-primary" />

        </div>


        <div className="min-w-0">

          <h3 className="font-display font-bold text-base sm:text-lg text-[hsl(148,38%,16%)]">
            {title}
          </h3>

          <p className="text-xs sm:text-sm text-muted-foreground mt-0.5">
            {subtitle}
          </p>

        </div>

      </div>


      {open ? (
        <ChevronUp className="h-5 w-5 text-muted-foreground shrink-0" />
      ) : (
        <ChevronDown className="h-5 w-5 text-muted-foreground shrink-0" />
      )}

    </button>
  );
}


export default function FarmManagement() {
  const {
    farms,
    farm,
    farmId,
    selectFarm,
    refresh,
  } = useFarm();


  const qc =
    useQueryClient();


  const [
    params,
    setParams,
  ] = useSearchParams();


  const isNew =
    params.get("new") === "1";


  const [
    form,
    setForm,
  ] = useState(empty);


  const [
    busy,
    setBusy,
  ] = useState(false);


  const [
    importBusy,
    setImportBusy,
  ] = useState(false);


  const [
    importCommitBusy,
    setImportCommitBusy,
  ] = useState(false);


  const [
    importResult,
    setImportResult,
  ] = useState(null);


  const [
    grassOpen,
    setGrassOpen,
  ] = useState(true);


  const [
    locationOpen,
    setLocationOpen,
  ] = useState(false);


  const [
    pastureBaseOpen,
    setPastureBaseOpen,
  ] = useState(false);


  useEffect(() => {
    if (isNew) {
      setForm(empty);

      setImportResult(
        null
      );

      return;
    }


    if (farm) {
      const norm = {
        ...empty,
        ...farm,
      };


      [
        "grass_residual",
        "pre_grazing_min",
        "pre_grazing_max",
      ].forEach(
        (key) => {
          if (
            norm[key] == null
          ) {
            norm[key] =
              "";
          }
        }
      );


      setForm(norm);
    }
  }, [
    farm,
    isNew,
  ]);


  const set = (
    key,
    value
  ) => {
    setForm(
      (
        current
      ) => ({
        ...current,
        [key]:
          value,
      })
    );
  };


  const num =
    (key) =>
    (event) => {
      set(
        key,
        event.target.value ===
          ""
          ? 0
          : Number(
              event.target.value
            )
      );
    };


  const save =
    async () => {
      if (
        !form.name
          ?.trim()
      ) {
        toast.error(
          "Please enter a farm name."
        );

        return;
      }


      setBusy(true);


      const payload = {
        ...form,
        name:
          form.name.trim(),
      };


      [
        "grass_residual",
        "pre_grazing_min",
        "pre_grazing_max",
      ].forEach(
        (key) => {
          payload[key] =
            form[key] ===
              "" ||
            form[key] ==
              null
              ? null
              : Number(
                  form[key]
                );
        }
      );


      try {
        if (isNew) {
          const {
            data,
          } =
            await api.post(
              "/farms",
              payload
            );


          await refresh();

          selectFarm(
            data.id
          );

          setParams({});

          toast.success(
            "Farm created"
          );

        } else {
          await api.put(
            `/farms/${farmId}`,
            payload
          );


          await refresh();

          qc.invalidateQueries();

          toast.success(
            "Farm updated"
          );
        }

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setBusy(false);
      }
    };


  const importPastureBase =
    async (
      event
    ) => {
      const file =
        event.target
          .files?.[0];


      if (!file) {
        return;
      }


      if (!farmId) {
        toast.error(
          "Please save the farm before importing paddocks."
        );

        event.target.value =
          "";

        return;
      }


      if (
        !file.name
          .toLowerCase()
          .endsWith(
            ".pdf"
          )
      ) {
        toast.error(
          "Please choose a PDF file."
        );

        event.target.value =
          "";

        return;
      }


      const data =
        new FormData();


      data.append(
        "file",
        file
      );


      setImportBusy(
        true
      );


      setImportResult(
        null
      );


      try {
        const response =
          await api.post(
            `/farms/${farmId}/imports/pasturebase/analyse`,
            data,
            {
              headers: {
                "Content-Type":
                  "multipart/form-data",
              },
            }
          );


        setImportResult(
          response.data
        );


        toast.success(
          "PastureBase report analysed"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setImportBusy(
          false
        );

        event.target.value =
          "";
      }
    };


  const commitPastureBase =
    async () => {
      const paddocks =
        importResult
          ?.paddocks ||
        [];


      if (!farmId) {
        toast.error(
          "Please save the farm before importing paddocks."
        );

        return;
      }


      if (
        paddocks.length ===
        0
      ) {
        toast.error(
          "No paddocks were detected in this report."
        );

        return;
      }


      setImportCommitBusy(
        true
      );


      try {
        const response =
          await api.post(
            `/farms/${farmId}/imports/pasturebase/commit`,
            {
              paddocks,

              report_type:
                importResult.report_type,

              report_year:
                importResult.report_year,

              farm_name:
                importResult.farm_name,

              herd_code:
                importResult.herd_code,

              source_filename:
                importResult.filename,
            }
          );


        setImportResult(
          (
            current
          ) => ({
            ...current,

            import_summary:
              response.data,
          })
        );


        await qc.invalidateQueries();


        toast.success(
          response.data
            .message ||
            "PastureBase paddocks imported"
        );

      } catch (error) {
        toast.error(
          formatApiErrorDetail(
            error.response
              ?.data
              ?.detail
          )
        );

      } finally {
        setImportCommitBusy(
          false
        );
      }
    };


  const remove =
    async () => {
      if (
        !window.confirm(
          `Delete ${farm?.name}? This removes its paddocks and feeds.`
        )
      ) {
        return;
      }


      await api.delete(
        `/farms/${farmId}`
      );


      await refresh();


      toast.success(
        "Farm deleted"
      );
    };


  const setupComplete =
    Boolean(
      form.name
        ?.trim()
    ) &&
    Number(
      form.grazing_area
    ) > 0 &&
    Number(
      form.cow_numbers
    ) > 0;


  const effectiveResidual =
    form.grass_residual ===
      "" ||
    form.grass_residual ==
      null
      ? BASIS_DEFAULTS[
          form.grass_basis ||
            "total_cover"
        ].residual
      : Number(
          form.grass_residual
        );


  const effectiveMin =
    form.pre_grazing_min ===
      "" ||
    form.pre_grazing_min ==
      null
      ? BASIS_DEFAULTS[
          form.grass_basis ||
            "total_cover"
        ].min
      : Number(
          form.pre_grazing_min
        );


  const effectiveMax =
    form.pre_grazing_max ===
      "" ||
    form.pre_grazing_max ==
      null
      ? BASIS_DEFAULTS[
          form.grass_basis ||
            "total_cover"
        ].max
      : Number(
          form.pre_grazing_max
        );


  return (
    <div className="max-w-6xl">

      <PageHeader
        title="Farm Management"
        subtitle="Manage the core farm information used across grass, nutrition, feed budgets and recommendations."
        action={
          !isNew &&
          farms.length >
            0 && (

            <Button
              variant="outline"
              className="rounded-full"
              data-testid="new-farm-button"
              onClick={() =>
                setParams({
                  new:
                    "1",
                })
              }
            >

              <Plus className="h-4 w-4 mr-1" />

              New farm

            </Button>

          )
        }
      />


      {/* ------------------------------------------------ */}
      {/* Farm profile summary                             */}
      {/* ------------------------------------------------ */}

      <Card className="overflow-hidden mb-5 sm:mb-6">

        <div className="grid grid-cols-1 lg:grid-cols-12">

          <div className="lg:col-span-5 p-5 sm:p-6 bg-primary/[0.04]">

            <div className="flex items-center gap-4">

              <div className="h-14 w-14 sm:h-16 sm:w-16 rounded-full bg-primary/10 grid place-items-center shrink-0">

                <Tractor className="h-7 w-7 sm:h-8 sm:w-8 text-primary" />

              </div>


              <div className="min-w-0">

                <h2 className="font-display text-xl sm:text-2xl font-extrabold text-[hsl(148,38%,16%)] truncate">

                  {form.name ||
                    "New farm"}

                </h2>


                <p className="text-sm text-muted-foreground mt-1">

                  {form.herd_type
                    ? form.herd_type
                        .charAt(
                          0
                        )
                        .toUpperCase() +
                      form.herd_type.slice(
                        1
                      )
                    : "Farm"}

                  {" · "}

                  {Number(
                    form.cow_numbers
                  ) || 0}{" "}

                  cows

                  {" · "}

                  {Number(
                    form.grazing_area
                  ) || 0}{" "}

                  ha

                </p>


                <div
                  className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-semibold mt-3 ${
                    setupComplete
                      ? "bg-primary/10 text-primary"
                      : "bg-[hsl(43,74%,52%)]/10 text-[hsl(38,70%,35%)]"
                  }`}
                >

                  {setupComplete ? (
                    <CheckCircle2 className="h-4 w-4" />
                  ) : (
                    <AlertTriangle className="h-4 w-4" />
                  )}


                  {setupComplete
                    ? "Farm setup complete"
                    : "Farm setup incomplete"}

                </div>

              </div>

            </div>

          </div>


          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x">

            <div className="p-4 sm:p-5 flex sm:flex-col gap-3 sm:gap-2 sm:items-center sm:text-center">

              <Tractor className="h-5 w-5 text-primary shrink-0" />

              <div>

                <p className="text-xs text-muted-foreground">
                  Herd type
                </p>

                <p className="font-semibold text-[hsl(148,38%,16%)] mt-1">
                  {form.herd_type
                    ? form.herd_type
                        .charAt(
                          0
                        )
                        .toUpperCase() +
                      form.herd_type.slice(
                        1
                      )
                    : "—"}
                </p>

              </div>

            </div>


            <div className="p-4 sm:p-5 flex sm:flex-col gap-3 sm:gap-2 sm:items-center sm:text-center">

              <Sprout className="h-5 w-5 text-primary shrink-0" />

              <div>

                <p className="text-xs text-muted-foreground">
                  Grazing area
                </p>

                <p className="font-semibold text-[hsl(148,38%,16%)] mt-1">

                  {Number(
                    form.grazing_area
                  ) || 0}{" "}

                  ha

                </p>

              </div>

            </div>


            <div className="p-4 sm:p-5 flex sm:flex-col gap-3 sm:gap-2 sm:items-center sm:text-center">

              <Target className="h-5 w-5 text-primary shrink-0" />

              <div>

                <p className="text-xs text-muted-foreground">
                  Cow numbers
                </p>

                <p className="font-semibold text-[hsl(148,38%,16%)] mt-1">

                  {Number(
                    form.cow_numbers
                  ) || 0}{" "}

                  cows

                </p>

              </div>

            </div>

          </div>

        </div>

      </Card>


      {/* ------------------------------------------------ */}
      {/* Main form cards                                  */}
      {/* ------------------------------------------------ */}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-6 mb-5 sm:mb-6">

        {/* Farm details */}

        <Card className="p-5 sm:p-6">

          <div className="flex items-center gap-3 mb-5">

            <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center">

              <Save className="h-5 w-5 text-primary" />

            </div>


            <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
              Farm details
            </h2>

          </div>


          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

            <div className="sm:col-span-2">

              <Label htmlFor="fname">
                Farm name
              </Label>

              <Input
                id="fname"
                data-testid="farm-name-input"
                value={
                  form.name
                }
                onChange={(
                  event
                ) =>
                  set(
                    "name",
                    event.target
                      .value
                  )
                }
                className="mt-1.5"
                placeholder="Green Valley Dairy"
              />

            </div>


            <div>

              <Label>
                Herd type
              </Label>

              <Select
                value={
                  form.herd_type
                }
                onValueChange={(
                  value
                ) =>
                  set(
                    "herd_type",
                    value
                  )
                }
              >

                <SelectTrigger
                  data-testid="herd-type-select"
                  className="mt-1.5"
                >

                  <SelectValue />

                </SelectTrigger>


                <SelectContent>

                  <SelectItem value="dairy">
                    Dairy
                  </SelectItem>

                  <SelectItem value="beef">
                    Beef
                  </SelectItem>

                  <SelectItem value="mixed">
                    Mixed
                  </SelectItem>

                </SelectContent>

              </Select>

            </div>


            <div>

              <Label>
                Currency
              </Label>

              <Select
                value={
                  form.currency ||
                  "EUR"
                }
                onValueChange={(
                  value
                ) =>
                  set(
                    "currency",
                    value
                  )
                }
              >

                <SelectTrigger
                  data-testid="currency-select"
                  className="mt-1.5"
                >

                  <SelectValue />

                </SelectTrigger>


                <SelectContent>

                  <SelectItem value="EUR">
                    Euro (€)
                  </SelectItem>

                  <SelectItem value="GBP">
                    Pound Sterling (£)
                  </SelectItem>

                </SelectContent>

              </Select>

            </div>


            <div>

              <Label htmlFor="area">
                Grazing area (ha)
              </Label>

              <Input
                id="area"
                type="number"
                min="0"
                data-testid="grazing-area-input"
                value={
                  form.grazing_area
                }
                onChange={
                  num(
                    "grazing_area"
                  )
                }
                className="mt-1.5"
              />

            </div>


            <div>

              <Label htmlFor="cows">
                Cow numbers
              </Label>

              <Input
                id="cows"
                type="number"
                min="0"
                data-testid="cow-numbers-input"
                value={
                  form.cow_numbers
                }
                onChange={
                  num(
                    "cow_numbers"
                  )
                }
                className="mt-1.5"
              />

            </div>

          </div>

        </Card>


        {/* Production targets */}

        <Card className="p-5 sm:p-6">

          <div className="flex items-center gap-3 mb-5">

            <div className="h-10 w-10 rounded-full bg-primary/10 grid place-items-center">

              <Target className="h-5 w-5 text-primary" />

            </div>


            <h2 className="font-display text-lg font-bold text-[hsl(148,38%,16%)]">
              Production targets
            </h2>

          </div>


          <div className="space-y-4">

            <div>

              <Label htmlFor="yield">
                Target milk yield (L/cow/day)
              </Label>

              <Input
                id="yield"
                type="number"
                min="0"
                step="0.1"
                data-testid="milk-yield-input"
                value={
                  form.target_milk_yield
                }
                onChange={
                  num(
                    "target_milk_yield"
                  )
                }
                className="mt-1.5"
              />

            </div>


            <div>

              <Label htmlFor="intake">
                Intake per cow (kg DM/day)
              </Label>

              <Input
                id="intake"
                type="number"
                min="0"
                step="0.1"
                data-testid="intake-input"
                value={
                  form.intake_per_cow
                }
                onChange={
                  num(
                    "intake_per_cow"
                  )
                }
                className="mt-1.5"
              />

            </div>


            <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-4">

              <div className="flex items-start gap-3">

                <Info className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />

                <p className="text-xs sm:text-sm text-blue-900 leading-relaxed">

                  These targets are used across Farm Hand to calculate feed demand, grass demand and nutritional requirements.

                </p>

              </div>

            </div>

          </div>

        </Card>

      </div>


      {/* ------------------------------------------------ */}
      {/* Grass settings                                   */}
      {/* ------------------------------------------------ */}

      <Card className="mb-4 overflow-hidden">

        <SectionHeader
          icon={
            Sprout
          }
          title="Grass settings"
          subtitle="Measurement basis, residuals and pre-grazing targets"
          open={
            grassOpen
          }
          onClick={() =>
            setGrassOpen(
              !grassOpen
            )
          }
        />


        {grassOpen && (

          <div className="border-t px-4 sm:px-5 pb-5 pt-5">

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">

              <div className="sm:col-span-3">

                <Label>
                  Measurement basis
                </Label>

                <Select
                  value={
                    form.grass_basis
                  }
                  onValueChange={(
                    value
                  ) =>
                    set(
                      "grass_basis",
                      value
                    )
                  }
                >

                  <SelectTrigger
                    data-testid="grass-basis-select"
                    className="mt-1.5"
                  >

                    <SelectValue />

                  </SelectTrigger>


                  <SelectContent>

                    <SelectItem value="total_cover">
                      Total cover to ground — residual 1500, target 2800–3000
                    </SelectItem>

                    <SelectItem value="above_4cm">
                      Cover above 4 cm — no residual, target 1300–1600
                    </SelectItem>

                  </SelectContent>

                </Select>

              </div>


              <div>

                <Label htmlFor="resid">
                  Residual (kg DM/ha)
                </Label>

                <Input
                  id="resid"
                  type="number"
                  data-testid="grass-residual-input"
                  value={
                    form.grass_residual
                  }
                  onChange={(
                    event
                  ) =>
                    set(
                      "grass_residual",
                      event.target
                        .value
                    )
                  }
                  className="mt-1.5"
                  placeholder={String(
                    BASIS_DEFAULTS[
                      form.grass_basis ||
                        "total_cover"
                    ]
                      .residual
                  )}
                />

              </div>


              <div>

                <Label htmlFor="pgmin">
                  Pre-grazing min
                </Label>

                <Input
                  id="pgmin"
                  type="number"
                  data-testid="pre-grazing-min-input"
                  value={
                    form.pre_grazing_min
                  }
                  onChange={(
                    event
                  ) =>
                    set(
                      "pre_grazing_min",
                      event.target
                        .value
                    )
                  }
                  className="mt-1.5"
                  placeholder={String(
                    BASIS_DEFAULTS[
                      form.grass_basis ||
                        "total_cover"
                    ].min
                  )}
                />

              </div>


              <div>

                <Label htmlFor="pgmax">
                  Pre-grazing max
                </Label>

                <Input
                  id="pgmax"
                  type="number"
                  data-testid="pre-grazing-max-input"
                  value={
                    form.pre_grazing_max
                  }
                  onChange={(
                    event
                  ) =>
                    set(
                      "pre_grazing_max",
                      event.target
                        .value
                    )
                  }
                  className="mt-1.5"
                  placeholder={String(
                    BASIS_DEFAULTS[
                      form.grass_basis ||
                        "total_cover"
                    ].max
                  )}
                />

              </div>

            </div>


            <div className="flex flex-wrap gap-2 mt-4">

              <span className="rounded-full bg-secondary px-3 py-1.5 text-xs">

                Residual:{" "}

                <strong>
                  {effectiveResidual.toLocaleString()}{" "}
                  kg DM/ha
                </strong>

              </span>


              <span className="rounded-full bg-secondary px-3 py-1.5 text-xs">

                Target:{" "}

                <strong>

                  {effectiveMin.toLocaleString()}

                  {"–"}

                  {effectiveMax.toLocaleString()}

                  {" kg DM/ha"}

                </strong>

              </span>

            </div>


            <p className="text-xs sm:text-sm text-muted-foreground mt-4 flex items-start gap-2">

              <Info className="h-4 w-4 text-primary shrink-0 mt-0.5" />

              <span>

                {form.grass_basis ===
                "above_4cm"
                  ? "Grass is measured above 4 cm, so no post-grazing residual is subtracted."
                  : "Grass is measured to ground, so the selected post-grazing residual is subtracted when calculating available pasture."}

                {" "}

                The two measurement systems are never mixed.

              </span>

            </p>

          </div>

        )}

      </Card>


      {/* ------------------------------------------------ */}
      {/* Location                                         */}
      {/* ------------------------------------------------ */}

      <Card className="mb-4 overflow-hidden">

        <SectionHeader
          icon={
            CloudSun
          }
          title="Location & weather"
          subtitle="Latitude and longitude used for local weather predictions"
          open={
            locationOpen
          }
          onClick={() =>
            setLocationOpen(
              !locationOpen
            )
          }
        />


        {locationOpen && (

          <div className="border-t px-4 sm:px-5 pb-5 pt-5">

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div>

                <Label htmlFor="lat">
                  Latitude
                </Label>

                <Input
                  id="lat"
                  type="number"
                  step="0.0001"
                  data-testid="lat-input"
                  value={
                    form.latitude
                  }
                  onChange={
                    num(
                      "latitude"
                    )
                  }
                  className="mt-1.5"
                />

              </div>


              <div>

                <Label htmlFor="lon">
                  Longitude
                </Label>

                <Input
                  id="lon"
                  type="number"
                  step="0.0001"
                  data-testid="lon-input"
                  value={
                    form.longitude
                  }
                  onChange={
                    num(
                      "longitude"
                    )
                  }
                  className="mt-1.5"
                />

              </div>

            </div>


            <div className="rounded-xl bg-secondary/50 p-4 mt-4">

              <div className="flex items-start gap-3">

                <MapPin className="h-5 w-5 text-primary shrink-0" />

                <p className="text-xs sm:text-sm text-muted-foreground">

                  Farm Hand uses this location for weather forecasts and weather-driven grass-growth calculations.

                </p>

              </div>

            </div>

          </div>

        )}

      </Card>


      {/* ------------------------------------------------ */}
      {/* PastureBase                                      */}
      {/* ------------------------------------------------ */}

      {!isNew && farm && (

        <Card className="mb-6 overflow-hidden">

          <SectionHeader
            icon={
              FileText
            }
            title="Import from PastureBase"
            subtitle="Import farm and paddock information from a PastureBase report"
            open={
              pastureBaseOpen
            }
            onClick={() =>
              setPastureBaseOpen(
                !pastureBaseOpen
              )
            }
          />


          {pastureBaseOpen && (

            <div className="border-t px-4 sm:px-5 pb-5 pt-5">

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                <div>

                  <p className="text-sm text-muted-foreground">

                    Upload a PastureBase PDF to detect farm and paddock information. Nothing will be saved until you review it.

                  </p>

                </div>


                <div>

                  <Input
                    id="pasturebase-upload"
                    type="file"
                    accept="application/pdf,.pdf"
                    className="hidden"
                    onChange={
                      importPastureBase
                    }
                    disabled={
                      importBusy
                    }
                  />


                  <Button
                    type="button"
                    variant="outline"
                    className="rounded-full"
                    disabled={
                      importBusy
                    }
                    onClick={() =>
                      document
                        .getElementById(
                          "pasturebase-upload"
                        )
                        ?.click()
                    }
                  >

                    <Upload className="h-4 w-4 mr-2" />

                    {importBusy
                      ? "Analysing..."
                      : "Choose PastureBase PDF"}

                  </Button>

                </div>

              </div>


              {importResult && (

                <div className="mt-5 rounded-xl border p-4">

                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">

                    <div>

                      <h3 className="font-semibold text-[hsl(148,38%,16%)]">
                        Report analysed successfully
                      </h3>

                      <p className="text-sm text-muted-foreground mt-1">
                        Review the detected paddocks before importing them.
                      </p>

                    </div>


                    <div className="rounded-full bg-secondary px-3 py-1 text-sm font-semibold whitespace-nowrap">

                      {importResult.paddock_count ||
                        0}{" "}

                      paddocks detected

                    </div>

                  </div>


                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4 text-sm">

                    <div>

                      <span className="text-muted-foreground">
                        File:
                      </span>{" "}

                      {importResult.filename}

                    </div>


                    <div>

                      <span className="text-muted-foreground">
                        Pages:
                      </span>{" "}

                      {importResult.page_count}

                    </div>


                    <div>

                      <span className="text-muted-foreground">
                        Farm:
                      </span>{" "}

                      {importResult.farm_name ||
                        "Not detected"}

                    </div>


                    <div>

                      <span className="text-muted-foreground">
                        Report year:
                      </span>{" "}

                      {importResult.report_year ||
                        "Not detected"}

                    </div>


                    <div>

                      <span className="text-muted-foreground">
                        Herd code:
                      </span>{" "}

                      {importResult.herd_code ||
                        "Not detected"}

                    </div>


                    <div>

                      <span className="text-muted-foreground">
                        Report type:
                      </span>{" "}

                      {importResult.report_type}

                    </div>

                  </div>


                  {importResult
                    .warnings
                    ?.length >
                    0 && (

                    <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 p-3">

                      {importResult.warnings.map(
                        (
                          warning,
                          index
                        ) => (

                          <p
                            key={`${warning}-${index}`}
                            className="text-sm text-amber-900"
                          >
                            {warning}
                          </p>

                        )
                      )}

                    </div>

                  )}


                  {importResult
                    .paddocks
                    ?.length >
                  0 ? (

                    <>

                      <div className="mt-5 overflow-hidden rounded-lg border">

                        <div className="max-h-80 overflow-auto">

                          <table className="min-w-[600px] w-full text-sm">

                            <thead className="sticky top-0 bg-muted">

                              <tr className="border-b">

                                <th className="px-3 py-2 text-left font-semibold">
                                  Paddock
                                </th>

                                <th className="px-3 py-2 text-right font-semibold">
                                  Area (ha)
                                </th>

                                <th className="px-3 py-2 text-right font-semibold">
                                  Grass grown
                                </th>

                              </tr>

                            </thead>


                            <tbody>

                              {importResult.paddocks.map(
                                (
                                  paddock,
                                  index
                                ) => (

                                  <tr
                                    key={`${paddock.name}-${index}`}
                                    className="border-b last:border-b-0"
                                  >

                                    <td className="px-3 py-2">
                                      {paddock.name}
                                    </td>

                                    <td className="px-3 py-2 text-right">

                                      {Number(
                                        paddock.area ||
                                          0
                                      ).toFixed(
                                        2
                                      )}

                                    </td>

                                    <td className="px-3 py-2 text-right">

                                      {paddock.annual_data
                                        ?.total_kg_dm_ha
                                        ? `${Number(
                                            paddock
                                              .annual_data
                                              .total_kg_dm_ha
                                          ).toLocaleString()} kg DM/ha`
                                        : "—"}

                                    </td>

                                  </tr>

                                )
                              )}

                            </tbody>

                          </table>

                        </div>

                      </div>


                      <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

                        <p className="text-xs text-muted-foreground">

                          Existing paddocks with matching names will be updated. Current grass-cover measurements will not be overwritten by this annual report.

                        </p>


                        <Button
                          type="button"
                          onClick={
                            commitPastureBase
                          }
                          disabled={
                            importCommitBusy
                          }
                          className="rounded-full shrink-0"
                        >

                          <Upload className="h-4 w-4 mr-2" />

                          {importCommitBusy
                            ? "Importing..."
                            : `Import ${importResult.paddocks.length} paddocks`}

                        </Button>

                      </div>

                    </>

                  ) : (

                    <div className="mt-4 rounded-md bg-muted p-3 text-sm">

                      No paddocks were detected in this report.

                    </div>

                  )}


                  {importResult
                    .import_summary && (

                    <div className="mt-4 rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-900">

                      <p className="font-semibold">
                        Import completed
                      </p>

                      <p className="mt-1">

                        {importResult
                          .import_summary
                          .created}{" "}

                        new paddocks created and{" "}

                        {importResult
                          .import_summary
                          .updated}{" "}

                        existing paddocks updated.

                      </p>

                      <p className="mt-1">

                        Open Paddock & Grass to review the imported paddocks and enter a current grass measurement.

                      </p>

                    </div>

                  )}

                </div>

              )}

            </div>

          )}

        </Card>

      )}


      {/* ------------------------------------------------ */}
      {/* Bottom actions                                   */}
      {/* ------------------------------------------------ */}

      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pb-4">

        {!isNew &&
          farm && (

          <Button
            variant="outline"
            className="rounded-full text-destructive border-destructive/20 hover:bg-destructive/5"
            data-testid="delete-farm-button"
            onClick={
              remove
            }
          >

            <Trash2 className="h-4 w-4 mr-2" />

            Delete farm

          </Button>

        )}


        <Button
          onClick={
            save
          }
          disabled={
            busy
          }
          className="rounded-full px-6 sm:min-w-[190px]"
          data-testid="save-farm-button"
        >

          <Save className="h-4 w-4 mr-2" />

          {busy
            ? "Saving..."
            : isNew
              ? "Create farm"
              : "Save changes"}

        </Button>

      </div>

    </div>
  );
}